import { createFileRoute } from "@tanstack/react-router"
import React, { useEffect, useState, useCallback, useMemo } from "react"
import { getMapStations } from "@/services/portalApi"
import { Station } from "@/services/types"
import { toast } from "react-hot-toast"

import SearchControls from "@/components/coverage-map/SearchControls"
import MapContent from "@/components/coverage-map/MapContent"
import { haversineDistance } from "@/utils/haversine"

export const Route = createFileRoute("/_auth/coverage-map/")({
  component: () => <RTKMap />,
  staticData: {
    title: "Nx RTK Coverage Map",
  },
})

const RTKMap: React.FC = () => {
  const [stations, setStations] = useState<Station[]>([])
  const [loading, setLoading] = useState(true)
  const [searchLocation, setSearchLocation] = useState<{ lat: number; lon: number } | null>(null)
  const [address, setAddress] = useState("")
  const [isMetric, setIsMetric] = useState(true)
  const [nearestStations, setNearestStations] = useState<{ dist: number; index: number; healthy: boolean }[]>([])
  const [selectedStationIndex, setSelectedStationIndex] = useState(0)
  const [showCoverage, setShowCoverage] = useState(true)

  useEffect(() => {
    const fetchStations = async () => {
      try {
        const stationsData = await getMapStations()
        if (stationsData) {
          // Remove potential duplicates and ensure valid coords
          setStations(stationsData)
        }
      } catch (e) {
        console.error("Failed to fetch stations", e)
        toast.error("Unable to load coverage map data. Please refresh or try again later.")
      } finally {
        setLoading(false)
      }
    }
    fetchStations()
  }, [])

  const updateNearest = useCallback((lat: number, lon: number) => {
    setSearchLocation({ lat, lon })
    if (stations.length === 0) return

    const withDist = stations.map((s, idx) => ({
      dist: haversineDistance(s.lat, s.lon, lat, lon),
      index: idx,
      healthy: s.healthy,
    }))

    // Top 3 closest stations regardless of health — shown in the selector
    const top3 = withDist
      .sort((a, b) => a.dist - b.dist)
      .slice(0, 3)
      .map(s => ({ dist: s.dist, index: s.index, healthy: s.healthy }))
    setNearestStations(top3)
    setSelectedStationIndex(0)
  }, [stations])

  const handleMapClick = (lat: number, lon: number) => {
    updateNearest(lat, lon)
  }

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!address) return

    // Quick check if it's "lat, lon" format
    const coords = address.split(',').map(s => parseFloat(s.trim()))
    if (
      coords.length === 2 &&
      !isNaN(coords[0]) && !isNaN(coords[1]) &&
      coords[0] >= -90 && coords[0] <= 90 &&
      coords[1] >= -180 && coords[1] <= 180
    ) {
      updateNearest(coords[0], coords[1])
      return
    }

    try {
      // Use Stadia Maps Search
      const res = await fetch(`https://api.stadiamaps.com/geocoding/v1/search?api_key=${import.meta.env.VITE_STADIA_MAPS_API_KEY}&text=${encodeURIComponent(address)}&limit=1`)
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          toast.error("Geocoding authentication failed. Please contact support.")
        } else if (res.status === 429) {
          toast.error("Too many search requests. Please wait a moment and try again.")
        } else {
          toast.error("Geocoding service error. Please try again later.")
        }
        return
      }
      const data = await res.json()
      if (data.features && data.features.length > 0) {
        const feature = data.features[0]
        updateNearest(feature.geometry.coordinates[1], feature.geometry.coordinates[0])
      } else {
        toast.error("Location not found. Please try a more specific address or coordinates.")
      }
    } catch (err) {
      console.error(err)
      toast.error("An error occurred while searching. Please try again.")
    }
  }

  const selectedStation = useMemo(() => {
    if (nearestStations.length > 0 && nearestStations[selectedStationIndex]) {
      return stations[nearestStations[selectedStationIndex].index]
    }
    return null
  }, [nearestStations, selectedStationIndex, stations])

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 140px)" }} className="bg-white rounded-lg overflow-hidden shadow-sm">
      <SearchControls
        address={address}
        setAddress={setAddress}
        handleSearch={handleSearch}
        onSelectCoords={(lat, lon, display) => {
          setAddress(display)
          updateNearest(lat, lon)
        }}
        isMetric={isMetric}
        setIsMetric={setIsMetric}
        nearestStations={nearestStations}
        selectedStationIndex={selectedStationIndex}
        onSelectStation={setSelectedStationIndex}
        showCoverage={showCoverage}
        setShowCoverage={setShowCoverage}
      />

      <div style={{ position: "relative", flex: 1, minHeight: 0 }}>
        <MapContent
          stations={stations}
          searchLocation={searchLocation}
          selectedStation={selectedStation}
          onMapClick={handleMapClick}
          loading={loading}
          isMetric={isMetric}
          showCoverage={showCoverage}
        />
      </div>

      <div className="px-6 py-3 bg-white/80 border-t border-gray-200 text-xs text-gray-500 leading-relaxed">
        <p>Coverage and performance depend on factors such as receiver and antenna characteristics, environment and use-case dynamics.</p>
        <p>For coverage in Australia, China, India, and Singapore, please <a href="https://www.swiftnav.com/contact-us" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-700">contact us</a>.</p>
      </div>
    </div>
  )
}
